import { useEffect, useRef, useState } from 'react';
import { createTurnstileLoginState } from './turnstileState';

export default function useTurnstileChallenge({ enabled = true, siteKey, action }) {
  const widgetContainerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const challengeRef = useRef(null);
  if (!challengeRef.current) challengeRef.current = createTurnstileLoginState();
  const challenge = challengeRef.current;
  const [challengeView, setChallengeView] = useState(challenge.snapshot());
  const [widgetRevision, setWidgetRevision] = useState(0);

  useEffect(() => challenge.subscribe(setChallengeView), [challenge]);
  useEffect(() => {
    challenge.setRebuildWidget(() => setWidgetRevision((revision) => revision + 1));
    return () => challenge.setRebuildWidget(null);
  }, [challenge]);

  useEffect(() => {
    if (!enabled) return undefined;
    const widgetRun = challenge.beginWidgetRender();
    if (!siteKey) return undefined;

    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || widgetIdRef.current != null || !widgetContainerRef.current || !window.turnstile?.render) return;
      try {
        widgetIdRef.current = window.turnstile.render(widgetContainerRef.current, {
          sitekey: siteKey,
          action,
          callback: (token) => challenge.tokenReceived(token, widgetRun),
          'expired-callback': () => challenge.widgetInvalidated('expired', widgetRun),
          // Netzwerk-/Widgetfehler warten auf den sichtbaren Retry, damit kein Reload-Loop entsteht.
          'error-callback': () => challenge.widgetInvalidated('widget-error', widgetRun, { requestRebuild: false }),
        });
      } catch {
        challenge.widgetInvalidated('widget-error', widgetRun, { requestRebuild: false });
      }
    };

    const scriptError = () => {
      if (script) script.dataset.voogoTurnstileState = 'failed';
      challenge.widgetInvalidated('widget-error', widgetRun, { requestRebuild: false });
    };
    const scriptLoaded = () => {
      if (!window.turnstile?.render) {
        scriptError();
        return;
      }
      if (script) script.dataset.voogoTurnstileState = 'ready';
      renderWidget();
    };
    let script = document.querySelector('script[src*="turnstile"]');
    // Solange die API fehlt, ist der Zustand jedes übernommenen Shared-Scripts unbekannt:
    // load/error kann bereits vor dieser SPA-Montage passiert sein. Darum wird genau ein
    // frisches, statusmarkiertes Script erzeugt statt an möglicherweise vergangene Events zu hängen.
    if (window.turnstile?.render) {
      renderWidget();
    } else {
      script?.remove();
      script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.dataset.voogoTurnstileState = 'loading';
      script.addEventListener('load', scriptLoaded);
      script.addEventListener('error', scriptError, { once: true });
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      script?.removeEventListener('load', scriptLoaded);
      script?.removeEventListener('error', scriptError);
      if (widgetIdRef.current != null && window.turnstile?.remove) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
      }
      widgetIdRef.current = null;
      // Das Script wird zwischen Login und Registrierung geteilt und deshalb nicht entfernt.
    };
  }, [challenge, enabled, siteKey, action, widgetRevision]);

  return { challenge, challengeView, widgetContainerRef };
}
